import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useSitStores} from "../../../stores";
import {generatePath, useNavigate} from "react-router";
import {LinearProgress, TablePagination} from "@mui/material";
import {INITIATIVE_APPROVAL_COLUMNS} from "./columns";
import {sitRoutes} from "../../routing/sitRoutes";
import {CurrencyComponent} from "../../../components/currency-component/CurrencyComponent";
import moment from "moment/moment";
import {MithraTablePaginationActions} from "../../../components/table-pagination/MithraTablePaginationActions";
import {SitDoiPill} from "../../components/design/SitDesignAtoms";
import type {Initiative} from "../../ApiTypes";

// Right-align the numeric / date columns (Cost savings, Cost avoidance, Last modified).
const RIGHT_COLS = [6, 7, 10];

export const InitiativeApprovalDashboard: React.FC = observer(() => {
    const {ccStore, initiativeListStore: store} = useSitStores();
    const navigate = useNavigate();

    useEffect(() => {
        store.reloadApprovalData(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const open = (row: Initiative) =>
        navigate(generatePath(sitRoutes.sit_initiative, {initiativeId: row.id, approval: 'approval'}));
    const result = store.initiativeApprovalListRM.result;

    return (
        <div className="initiative-approval-table-component sit-list">
            {store.initiativeApprovalListRM.busy && <LinearProgress variant="indeterminate"/>}

            <div className="sit-table-wrap">
                <table className="sit-table">
                    <thead>
                        <tr>
                            {INITIATIVE_APPROVAL_COLUMNS.map((c, i) => (
                                <th key={i} className={RIGHT_COLS.includes(i) ? 'right' : ''}
                                    style={{minWidth: c.minWidth}}>
                                    {c.txt}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    {result && <tbody>
                        {result.results.map((row: Initiative) => (
                            <tr key={row.id} onClick={() => open(row)} style={{cursor: 'pointer'}}>
                                <td>
                                    <div className="sit-row-title">{row.title}</div>
                                    <div className="sit-row-id">#{row.id}</div>
                                </td>
                                <td>{row.owner ? row.owner.name : 'No owner'}</td>
                                <td>{row.financial.map(f => f.companies.map(c => ccStore.getCompanyTitle(c)).join(', ')).join(', ')}</td>
                                <td>{ccStore.getCategoryL1(row.financial?.at(0)?.categories?.at(0) ?? 0)}</td>
                                <td>{ccStore.getLeverGroupTitle(row.lever_id)}</td>
                                <td><SitDoiPill doi={row.doi_status} approvalState={row.approved ? 'approved' : null}/></td>
                                <td className="right">
                                    <CurrencyComponent nDigits={3}
                                                       v={row.financial.reduce((sum, f) => sum + parseFloat(String(f.saving_amount)), 0) || 0}/>
                                </td>
                                <td className="right">
                                    <CurrencyComponent nDigits={3}
                                                       v={row.financial.reduce((sum, f) => sum + parseFloat(String(f.avoidance_amount)), 0) || 0}/>
                                </td>
                                <td>{row.approver ? row.approver.name : 'No approver'}</td>
                                <td>
                                    {row.approved
                                        ? <span className="sit-tag hue-good">Approved</span>
                                        : <span className="sit-tag hue-warn">Pending</span>}
                                </td>
                                <td className="right">{row.updated ? moment(row.updated).format('L') : ''}</td>
                                <td>{row.responsible?.name}</td>
                            </tr>
                        ))}
                    </tbody>}
                </table>
            </div>

            <TablePagination
                component="div"
                count={result?.count ?? 0}
                rowsPerPage={store.approval_per_page}
                page={store.approval_page}
                onPageChange={(_e, page: number) => store.setApprovalPage(page)}
                onRowsPerPageChange={(event) => store.setApprovalPerPage(parseInt(event.target.value) || 0)}
                ActionsComponent={MithraTablePaginationActions}
            />
        </div>
    );
});
